import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'modal', 'search', 'results', 'prev', 'next', 'pageinfo', 'addLink'
  ]

  static values = {
    searchUrl: String
  }

  connect() {
    this.page = 1
    this.pages = 1
    this.items = []
    this.targetRow = null
    this.debounceTimer = null
    this.abortController = null
    this._onKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.cancelDebounce()
    this.abortFetch()
    document.removeEventListener('keydown', this._onKeydown)
  }

  openForNew(event) {
    if (event) event.preventDefault()
    this.targetRow = null
    this.open()
  }

  openForRow(event) {
    if (event) event.preventDefault()
    this.targetRow = event.currentTarget.closest('.partner-form__logo')
    this.open()
  }

  open() {
    this.modalTarget.hidden = false
    document.addEventListener('keydown', this._onKeydown)
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
      this.searchTarget.select()
    }
    this.resetAndSearch()
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.hidden = true
    document.removeEventListener('keydown', this._onKeydown)
    this.targetRow = null
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close()
  }

  resetAndSearch(event) {
    if (event) event.preventDefault()
    this.page = 1
    this.search()
  }

  resetAndSearchDebounced() {
    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => this.resetAndSearch(), 300)
  }

  async search() {
    this.showLoading()

    try {
      this.abortFetch()
      this.abortController = new AbortController()

      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('page', this.page)
      const q = this.hasSearchTarget ? this.searchTarget.value.trim() : ''
      if (q) url.searchParams.set('q', q)

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Search failed')

      const data = await response.json()
      this.items = data.partners || []
      this.pages = (data.pagination && data.pagination.pages) || 1
      this.page = (data.pagination && data.pagination.page) || 1
      this.count = (data.pagination && data.pagination.count) || 0
      this.renderResults()
      this.renderPagination()
    } catch (error) {
      if (error.name !== 'AbortError') this.renderError()
    }
  }

  renderResults() {
    if (this.items.length === 0) {
      this.resultsTarget.innerHTML =
        '<div class="partner-logo-library__empty">Ничего не найдено</div>'
      return
    }

    this.resultsTarget.innerHTML = this.items.map((item, index) => `
      <button type="button"
              class="partner-logo-library__item"
              data-action="admin--partner-logo-library#selectItem"
              data-index="${index}">
        <span class="partner-logo-library__thumb">
          <img src="${this.escapeAttr(item.logo_url)}" alt="" loading="lazy">
        </span>
        <span class="partner-logo-library__name">${this.escape(item.name)}</span>
      </button>
    `).join('')
  }

  renderPagination() {
    if (this.hasPageinfoTarget) {
      this.pageinfoTarget.textContent = this.count
        ? `Стр. ${this.page} из ${this.pages} · всего ${this.count}`
        : ''
    }
    if (this.hasPrevTarget) this.prevTarget.disabled = this.page <= 1
    if (this.hasNextTarget) this.nextTarget.disabled = this.page >= this.pages
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1
      this.search()
    }
  }

  nextPage() {
    if (this.page < this.pages) {
      this.page += 1
      this.search()
    }
  }

  selectItem(event) {
    const item = this.items[parseInt(event.currentTarget.dataset.index, 10)]
    if (!item) return

    const row = this.ensureTargetRow()
    if (!row) {
      window.alert('Не удалось добавить логотип')
      return
    }

    this.applyToRow(row, item)
    this.close()
  }

  ensureTargetRow() {
    if (this.targetRow && document.contains(this.targetRow)) {
      return this.targetRow
    }

    if (!this.hasAddLinkTarget) return null

    this.addLinkTarget.click()
    const rows = this.element.querySelectorAll('.partner-form__logo')
    return rows[rows.length - 1] || null
  }

  applyToRow(row, item) {
    const partnerId = row.querySelector('[data-partner-logo-partner-id]')
    if (partnerId) partnerId.value = item.id

    const signedId = row.querySelector('[data-partner-logo-signed-id]')
    if (signedId) signedId.value = ''

    const fileInput = row.querySelector('input[type="file"]')
    if (fileInput) fileInput.value = ''

    const preview = row.querySelector('[data-admin--file-input-target="preview"]')
    const placeholder = row.querySelector('[data-admin--file-input-target="placeholder"]')
    if (preview) {
      preview.src = item.logo_url
      preview.hidden = false
    }
    if (placeholder) placeholder.hidden = true

    const name = row.querySelector('[data-admin--file-input-target="name"]')
    if (name) name.textContent = item.name || 'Из библиотеки'

    const urlInput = row.querySelector('input[name*="[url]"]')
    if (urlInput && item.link) urlInput.value = item.link
  }

  showLoading() {
    this.resultsTarget.innerHTML =
      '<div class="partner-logo-library__empty">Загрузка…</div>'
  }

  renderError() {
    this.resultsTarget.innerHTML =
      '<div class="partner-logo-library__empty">Ошибка загрузки</div>'
  }

  cancelDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escape(text) {
    if (text === null || text === undefined) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    return this.escape(text).replace(/"/g, '&quot;')
  }
}
